import CloudImage from "@/lib/components/CloudImage";
import { CLOUDINARY_FOLDER } from "@/lib/constants/cloudinary";
import { useLanguage } from "@/lib/hooks/useLanguage";
import { useTranslation } from "@/lib/i18n/client";
import MultiRangeSlider, {
    MultiRangeSliderOnChangeEvent,
    OnChangeMultiRangeSlider,
} from "./MultiRangeSlider/MultiRangeSlider";
import { useMemo, useState } from "react";
import Input from "@/lib/components/Input";
import { getLanguageData } from "@/lib/i18n/settings";
import { FILTER_MAX_PRICE, FILTER_MIN_PRICE } from "@/lib/constants/search";

type Props = {
    defaultMin?: number;
    defaultMax?: number;
    onChange: MultiRangeSliderOnChangeEvent;
};

const PriceFilter = ({ defaultMin = FILTER_MIN_PRICE, defaultMax = FILTER_MAX_PRICE, onChange }: Props) => {
    const lang = useLanguage();
    const { t } = useTranslation(lang, "search");
    const currencySign = getLanguageData(lang).currencySign;

    const formatCurrency = (val: number) => `${val} ${currencySign}`;

    const [minValue, setMinValue] = useState<number>(defaultMin);
    const [maxValue, setMaxValue] = useState<number>(defaultMax);

    const [minValueError, setMinValueError] = useState<boolean>(false);
    const [maxValueError, setMaxValueError] = useState<boolean>(false);

    // Whether the user clicked on the first time in the input field to enter a new number.
    const [minValueEmpty, setMinValueEmpty] = useState<boolean>(false);
    const [maxValueEmpty, setMaxValueEmpty] = useState<boolean>(false);

    // Whether the user is current in the Minimum input field or not.
    const [insideMinValue, setInsideMinValue] = useState<boolean>(false);
    const [insideMaxValue, setInsideMaxValue] = useState<boolean>(false);

    const minInputFieldValueFormattedOrNot = insideMinValue ? minValue : formatCurrency(minValue);
    const minInputFieldValue = minValueEmpty ? "" : minInputFieldValueFormattedOrNot;

    const maxInputFieldValueFormattedOrNot = insideMaxValue ? maxValue : formatCurrency(maxValue);
    const maxInputFieldValue = maxValueEmpty ? "" : maxInputFieldValueFormattedOrNot;

    const onChangeSlider = (props: OnChangeMultiRangeSlider) => {
        setMinValue(props.min);
        setMaxValue(props.max);
        onChange(props);
    };

    function extractNumericValue(input: string): number | null {
        const numericValueMatch = /(\d+(\.\d+)?)/.exec(input);

        if (numericValueMatch) {
            const numericValue = parseFloat(numericValueMatch[0]);
            return numericValue;
        } else {
            return null;
        }
    }

    const resetPriceRangeValueErrors = (currMinValue: number, currMaxValue: number) => {
        if (currMinValue < currMaxValue) {
            setMinValueError(false);
            setMaxValueError(false);
        }
    };

    const onMinPriceChange = (value: string): void => {
        if (value === "") {
            // use deletes all characters after entering something
            setMinValueError(true);
            setMinValueEmpty(true);
            return;
        }
        setMinValueEmpty(false);

        const minNumericValue = extractNumericValue(value);
        if (minNumericValue === null || minNumericValue < FILTER_MIN_PRICE) {
            // ignore invalid input like non-numeric characters or numbers
            // that make the filter value exceed the FILTER_MAX_PRICE
            return;
        }

        if (minNumericValue > maxValue) {
            setMinValueError(true);
        } else {
            setMinValueError(false);
        }

        setMinValue(minNumericValue);
        resetPriceRangeValueErrors(minNumericValue, maxValue);
        onChange({ min: minNumericValue, max: maxValue });
    };

    const onMaxPriceChange = (value: string): void => {
        if (value === "") {
            // use deletes all characters after entering something
            setMaxValueError(true);
            setMaxValueEmpty(true);
            return;
        }
        setMaxValueEmpty(false);

        const maxNumericValue = extractNumericValue(value);
        if (maxNumericValue === null || maxNumericValue > FILTER_MAX_PRICE) {
            // ignore invalid input like non-numeric characters or numbers
            // that make the filter value exceed the FILTER_MAX_PRICE
            return;
        }

        if (maxNumericValue < minValue) {
            setMaxValueError(true);
        } else {
            setMaxValueError(false);
        }

        setMaxValue(maxNumericValue);
        resetPriceRangeValueErrors(minValue, maxNumericValue);
        onChange({ min: minValue, max: maxNumericValue });
    };

    const onMinFocus = () => {
        setMinValueEmpty(true);
        setInsideMinValue(true);
    };

    const onMinBlur = () => {
        setInsideMinValue(false);
        setMinValueEmpty(false);
    };

    const onMaxFocus = () => {
        setMaxValueEmpty(true);
        setInsideMaxValue(true);
    };

    const onMaxBlur = () => {
        setInsideMaxValue(false);
        setMaxValueEmpty(false);
    };

    const priceImage = useMemo(
        () => (
            <div className="relative h-40">
                <CloudImage
                    src={`${CLOUDINARY_FOLDER}/static/pricegraph.png`}
                    alt=""
                    fill={true}
                    className="object-fill brightness-75"
                />
            </div>
        ),
        [],
    );

    return (
        <div className="grow pb-2">
            <div className="hidden lg:flex grow flex-row gap-2">
                <div className="flex flex-col justify-center">
                    <Input
                        customHeight="h-11"
                        value={minInputFieldValue}
                        className={`w-28 ${minValueError ? "border-red-500" : ""}`}
                        onFocus={onMinFocus}
                        onBlur={onMinBlur}
                        onChange={(e) => onMinPriceChange(e.target.value)}
                    />
                    {t("filter.price-filter.min-price")}
                </div>
                <div className="flex flex-col grow">
                    {!minValueError && !maxValueError && (
                        <>
                            {priceImage}
                            <MultiRangeSlider min={minValue} max={maxValue} onChange={onChangeSlider} />
                        </>
                    )}
                </div>
                <div className="flex flex-col justify-center items-center">
                    <Input
                        customHeight="h-11"
                        value={maxInputFieldValue}
                        className={`w-28 ${maxValueError ? "border-red-500" : ""}`}
                        onFocus={onMaxFocus}
                        onBlur={onMaxBlur}
                        onChange={(e) => onMaxPriceChange(e.target.value)}
                    />
                    {t("filter.price-filter.max-price")}
                </div>
            </div>
            <div className="flex grow lg:hidden flex-col gap-3">
                <div className="flex flex-col grow">
                    {!minValueError && !maxValueError && (
                        <>
                            {priceImage}
                            <MultiRangeSlider min={minValue} max={maxValue} onChange={onChangeSlider} />
                        </>
                    )}
                </div>
                <div className="flex flex-row justify-between">
                    <div className="flex flex-col justify-center">
                        <Input
                            customHeight="h-11"
                            value={minInputFieldValue}
                            className={`w-28 ${minValueError ? "border-red-500" : ""}`}
                            onFocus={onMinFocus}
                            onBlur={onMinBlur}
                            onChange={(e) => onMinPriceChange(e.target.value)}
                        />
                        {t("filter.price-filter.min-price")}
                    </div>
                    <div className="flex flex-col justify-center items-center">
                        <Input
                            customHeight="h-11"
                            value={maxInputFieldValue}
                            className={`w-28 ${maxValueError ? "border-red-500" : ""}`}
                            onFocus={onMaxFocus}
                            onBlur={onMaxBlur}
                            onChange={(e) => onMaxPriceChange(e.target.value)}
                        />
                        {t("filter.price-filter.max-price")}
                    </div>
                </div>
            </div>
        </div>
    );
};
export default PriceFilter;
